<script setup lang="ts">
/**
 * The line under the chat header (docs/ai-mode.md):
 *  - AI in charge → a quiet pill "AI กำลังดูแล · ตั้งแต่ 18:42 · โดย พลอย" + "หยุด AI"
 *  - AI handed the chat back and nobody took it yet → warning banner: why + note + time + "รับต่อ" (= assign to me)
 */
import { computed } from 'vue'
import { HANDOFF_LABEL } from '../../api/aimode/types'
import { clockTime } from '../../lib/inbox'
import { useAiMode } from '../../stores/aimode'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const inbox = useInbox()
const aimode = useAiMode()

const conv = computed(() => inbox.convs[props.convId] ?? null)
const mode = computed(() => conv.value?.ai_mode ?? null)
const handoff = computed(() => (mode.value ? null : (conv.value?.ai_handoff ?? null)))
const busy = computed(() => !!aimode.busy[props.convId])
const label = computed(() => (handoff.value ? (HANDOFF_LABEL[handoff.value.code] ?? 'AI ส่งต่อให้พนักงาน') : ''))
const note = computed(() => {
  const n = handoff.value?.note?.trim() ?? ''
  return n && n !== label.value ? n : ''
})
</script>

<template>
  <div v-if="mode" class="amb on" role="status">
    <span class="amb-pill">
      <span class="amb-live" aria-hidden="true"></span>
      <Icon name="sparkles" />
      <b>AI กำลังดูแล</b>
      <span class="amb-sep" aria-hidden="true">·</span>
      <span>ตั้งแต่ <span class="mono">{{ clockTime(mode.since) }}</span></span>
      <span class="amb-sep" aria-hidden="true">·</span>
      <span class="amb-by">โดย {{ mode.by.name }}</span>
    </span>
    <span class="grow"></span>
    <button type="button" class="amb-stop" :disabled="busy" @click="aimode.setOn(convId, false)">
      <Icon name="pauseCircle" />หยุด AI
    </button>
  </div>

  <div v-else-if="handoff" class="amb hand" role="alert">
    <span class="amb-ic"><Icon name="handoff" /></span>
    <span class="amb-t">
      <b>AI ส่งต่อให้พนักงาน · {{ label }}</b>
      <span v-if="note" class="amb-note">{{ note }}</span>
    </span>
    <span class="amb-time mono" :title="'ส่งต่อเมื่อ ' + clockTime(handoff.at)">{{ clockTime(handoff.at) }}</span>
    <button type="button" class="amb-take" :disabled="busy" @click="aimode.accept(convId)">
      <Icon name="check" />รับต่อ
    </button>
  </div>
</template>

<style scoped>
.amb {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 6px 16px 6px 24px;
  border-bottom: 1px solid var(--line);
  font-size: 13px;
  line-height: 20px;
}
.amb.on {
  background: #f3faf6;
}
.amb-pill {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 6px;
  min-width: 0;
  color: #24453a;
}
.amb-pill b {
  font-weight: 600;
  color: var(--ac2);
}
.amb-pill :deep(.ic) {
  width: 15px;
  height: 15px;
  color: var(--ac);
}
.amb-live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ac);
  box-shadow: 0 0 0 3px rgba(14, 122, 92, 0.16);
  margin-right: 2px;
}
.amb-sep {
  color: #9fb3a9;
}
.amb-by {
  white-space: nowrap;
}
.amb-stop {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
  white-space: nowrap;
}
.amb-stop :deep(.ic) {
  width: 15px;
  height: 15px;
  color: var(--mut);
}
.amb-stop:hover:not(:disabled) {
  background: #f7f9f8;
}
.amb.hand {
  background: #fff8e6;
  border-bottom-color: #f0dca6;
  color: #6b4300;
}
.amb-ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fde9b8;
  color: #8a5100;
}
.amb-ic :deep(.ic) {
  width: 16px;
  height: 16px;
}
.amb-t {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.amb-t b {
  font-weight: 600;
  line-height: 20px;
}
.amb-note {
  font-size: 12.5px;
  line-height: 19px;
  color: #7a5a1e;
  overflow-wrap: anywhere;
}
.amb-time {
  flex: none;
  font-size: 12px;
  color: #8a6a2a;
}
.amb-take {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  border-radius: 8px;
  background: var(--ac);
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.amb-take :deep(.ic) {
  width: 15px;
  height: 15px;
}
.amb-take:hover:not(:disabled) {
  background: var(--ac2);
}
button:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
